import { useEffect, useReducer, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import {
  api, attachmentUrl, type AttachmentRead, type Card, type Preflight, type Started, type StoredEmail, type TextCheck,
} from '../lib/api';
import { runLine, type Run } from '../lib/approving';
import { ageOf, dayLocal, usd, whenBrisbane, whenLocal } from '../lib/time';
import {
  cardLook, isConsequential, who, DESTRUCTIVE_TYPES, decidedBy, approveAction, signupButtons, hasDraft, portalWait, waitLine, waitingQuestions,
} from '../lib/cards';
import { useResource } from '../lib/use-resource';
import type { ConfirmRequest } from './Confirm';
import './carddetail.css';
import { AnswersPanel, QuestionsList } from './AnswersPanel';
import { DraftWorkspace } from './DraftWorkspace';
import { EditsPanel } from './EditsPanel';
import {
  dirty as isDirty, editorReducer, initialEditor, keepDraft, keptDraft, keyAction, type Fields,
} from '../lib/editor';
import type { Layout } from '../lib/layout';

const session = () => { try { return window.sessionStorage; } catch { return null; } };
const fieldsOf = (c: Card): Fields => ({ subject: c.payload.subject ?? '', body: c.payload.body ?? '' });

/**
 * The opened card: everything needed to decide, on one screen.
 *
 * The order down the page is the order the question gets answered — has
 * anything changed since this was raised, what was looked at, what they wrote,
 * what we would say, what it would change — and the action bar is last because
 * it is what you do after reading, not before.
 */
export function CardDetail({ card, onConfirm, onChanged, me, operator = false, onBack, layout, onLayout, onStarted, run: running }: {
  card: Card;
  onConfirm: (r: ConfirmRequest) => void;
  onChanged: () => void;
  /** fix-79: the desk answered 202 — claimed, the run continues there; the Desk follows it and moves on. */
  onStarted?: (card: Card) => void;
  /** fix-79: this card's run, while the console follows it. */
  run?: Run | null;
  me: string | null;
  /** #217: Try now is offered to the operator only (the desk refuses anyone else). */
  operator?: boolean;
  onBack?: () => void;
  /** fix-74: the room around the card (lib/layout.ts), and what J K Q F ask of it. */
  layout?: Layout;
  onLayout?: (act: 'next' | 'prev' | 'queue' | 'focus') => void;
}) {
  const [, setLocation] = useLocation();
  const look = cardLook(card);
  const pending = card.status === 'pending';

  const pf = useResource<Preflight>(() => api.preflight(card.id), { deps: [card.id] });
  const email = useResource<{ email: StoredEmail }>(
    () => api.email(card.email_id as number),
    { enabled: card.email_id !== null, deps: [card.email_id] },
  );

  const [busy, setBusy] = useState<string | null>(null);
  // fix-74: in focus the card's context is a slide-over, closed by Esc, the scrim or its own button.
  const focus = layout?.focus ?? false;
  const [slideOpen, setSlideOpen] = useState(false);
  useEffect(() => { setSlideOpen(false); }, [card.id, focus]);
  const [draftFocused, setDraftFocused] = useState(false);
  useEffect(() => { setDraftFocused(false); }, [card.id]);
  const [problem, setProblem] = useState<string | null>(null);
  // #217: what Try now said when the portal still would not take it — not a failure, the card goes on waiting.
  const [stillWaiting, setStillWaiting] = useState<string | null>(null);
  useEffect(() => { setStillWaiting(null); }, [card.id]);
  const wait = portalWait(card);
  const editorRef = useRef<HTMLTextAreaElement>(null);

  // fix-73: the draft is the editor, always editable, no mode (lib/editor.ts). A fetch never overwrites unsaved
  // text; unsaved text is kept per card across leaving the screen.
  const written = hasDraft(card);
  const [ed, dispatch] = useReducer(editorReducer, initialEditor);
  const restored = useRef<number | null>(null);
  useEffect(() => { dispatch({ type: 'fetched', cardId: card.id, fields: fieldsOf(card) }); setProblem(null); },
    [card.id, card.payload.body, card.payload.subject]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (ed.cardId !== card.id || !ed.base || restored.current === card.id) return;
    restored.current = card.id;
    const kept = keptDraft(session(), card.id);
    if (kept && pending) dispatch({ type: 'restore', ...kept });
  }, [ed.cardId, ed.base, card.id, pending]);
  useEffect(() => { if (restored.current === ed.cardId) keepDraft(session(), ed); }, [ed]);
  const edited = written && isDirty(ed);

  // The rules line: the text in the editor, checked by the desk a moment after he stops typing.
  const [check, setCheck] = useState<{ for: string; result: TextCheck } | null>(null);
  useEffect(() => {
    if (!written || !pending || ed.cardId !== card.id) return;
    const text = ed.draft.body;
    const t = setTimeout(() => {
      api.check(card.id, text).then((result) => setCheck({ for: text, result })).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [written, pending, card.id, ed.cardId, ed.draft.body]);

  /** Save my version when there is one; true when the editor now matches the desk. */
  const saveIfEdited = async () => {
    if (!edited) return;
    const r = await api.edit(card.id, { body: ed.draft.body, subject: ed.draft.subject });
    dispatch({ type: 'finished', fields: fieldsOf(r.card) });
  };

  /** One editor write: saves unsaved edits first, then loads what the desk holds. */
  const write = async (phase: 'saving' | 'refining' | 'answering', fn: () => Promise<Card | null>): Promise<boolean> => {
    dispatch({ type: 'start', phase });
    setProblem(null);
    try {
      if (phase !== 'saving') await saveIfEdited();
      dispatch({ type: 'start', phase });
      const c = await fn();
      dispatch({ type: 'finished', fields: c ? fieldsOf(c) : undefined });
      onChanged();
      pf.refetch();
      return true;
    } catch (e) {
      const d = e as { message: string; next?: string | null };
      const msg = d.next ? `${d.message} — ${d.next}` : d.message;
      dispatch({ type: 'failed', error: msg });
      setProblem(msg);
      return false;
    }
  };

  const save = () => (edited ? write('saving', async () => (await api.edit(card.id, {
    body: ed.draft.body, subject: ed.draft.subject })).card) : Promise.resolve(false));
  const refineWith = (instruction: string) => write('refining', async () => (await api.refine(card.id, instruction)).card);
  const answer = (i: number, fact: string) => write('answering', async () => (await api.answers(card.id, { answers: [{ i, fact }] })).card);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setProblem(null);
    try {
      await fn();
      onChanged();
      pf.refetch();
    } catch (e) {
      const d = e as { message: string; next?: string | null };
      setProblem(d.next ? `${d.message} — ${d.next}` : d.message);
    } finally {
      setBusy(null);
    }
  };

  const approve = () => {
    const destructive = DESTRUCTIVE_TYPES.has(card.type);
    const send = (overrideReason?: string, sendUnanswered = false) => run('approve', async () => {
      // An edit is saved BEFORE the approval, not passed through it: the
      // original and the edit are both kept, and card_feedback is what the
      // drafter reads back to learn what it got wrong. Approve sends whatever
      // the editor holds (fix-73).
      await saveIfEdited();
      started(await api.approve(card.id, {
        ...(overrideReason ? { operator_override_reason: overrideReason } : {}),
        ...(sendUnanswered ? { send_unanswered: true } : {}),
      }));
    });

    // WHAT A REFUSAL SHOULD OFFER, decided in lib/cards.ts where it is tested.
    // "Try it anyway" used to appear on every refusal and could succeed on none
    // of them: a final one expires the card, and the one that IS overridable
    // never sent the reason the desk needs.
    const action = approveAction(pf.data);

    if (action.kind === 'dismiss') {
      // The card was right and the world moved. That is Dismiss's exact meaning,
      // and it keeps the class off the ladder's debit side — which Reject, or an
      // expiry that looked like a failure, would not.
      onConfirm({
        title: 'This card can no longer succeed',
        detail: `${action.reason} Approving would only expire it. Dismiss it instead — it is `
              + 'recorded, and it does not count against the class.',
        confirmLabel: 'Dismiss it',
        onConfirm: () => run('dismiss', () => api.dismiss(card.id, action.reason)),
      });
      return;
    }

    if (action.kind === 'explain') {
      // Fixed elsewhere — edit the draft, or deploy the missing spec. A button
      // here would only produce the same refusal again.
      setProblem(action.reason);
      return;
    }

    if (action.kind === 'override') {
      onConfirm({
        title: 'This account is closed',
        detail: `${action.reason} The desk will act on a closed account with a written reason, `
              + 'and the reason is recorded on the event.',
        confirmLabel: 'Act anyway',
        tone: 'danger',
        askReason: true,
        reasonLabel: 'Why this should go to a closed account (recorded)',
        onConfirm: (reason) => {
          // No reason, no override: the desk would refuse it, and an empty
          // string sent as a reason is an audit trail that says nothing.
          if (!reason?.trim()) { setProblem('An override needs a written reason.'); return; }
          return send(reason.trim());
        },
      });
      return;
    }

    // ADR-0343 (PSA #305): a question the reply leaves out is never answered — the reply promises no follow-up.
    const waiting = waitingQuestions(card);
    if (waiting.length) {
      const one = waiting.length === 1;
      onConfirm({
        title: `Send without answering ${one ? 'this question' : `these ${waiting.length} questions`}?`,
        detail: `The reply leaves ${one ? 'it' : 'them'} out and promises no follow-up, so the customer is never `
              + `answered: ${waiting.map((q) => `"${q.question}"`).join('; ')}. Answer on the card first, or send it as it is.`,
        confirmLabel: 'Send without answering',
        onConfirm: () => send(undefined, true),
      });
      return;
    }

    if (!destructive) { send(); return; }

    onConfirm({
      title: `${look.label}: this changes the account`,
      detail: `Approving runs the portal writes for ${who(card)}. Every step is read back from the portal afterwards.`,
      confirmLabel: `Approve ${look.label.toLowerCase()}`,
      tone: 'danger',
      typeToConfirm: 'approve',
      onConfirm: () => send(),
    });
  };

  const reject = () => onConfirm({
    title: 'Reject this card?',
    detail: 'The reason is recorded on the card and is the training signal the drafter reads back.',
    confirmLabel: 'Reject',
    tone: 'danger',
    askReason: true,
    reasonLabel: 'Why (this is the useful part)',
    onConfirm: (reason) => run('reject', () => api.reject(card.id, reason || 'no reason given')),
  });

  /**
   * THE THIRD VERB. Reject says the rule misfired; this says it did not, and
   * something else happened first — a stronger notice already went out, the
   * customer paid, the account closed.
   *
   * It matters because Reject is counted: `topup_reminder` sits at 1 approved
   * and 13 rejected, and every one of those thirteen was a duplicate T20 from
   * cutover day that v1 had already sent. Good cards, no verb that fitted, and
   * a class that can now never climb the ladder.
   */
  const dismiss = () => onConfirm({
    title: 'Dismiss this card?',
    detail: 'For a card that was right when it was raised and has been overtaken since. '
          + 'It is recorded, and unlike Reject it does not count against the class on the '
          + 'autonomy ladder and does not go to the drafter as training.',
    confirmLabel: 'Dismiss',
    askReason: true,
    reasonLabel: 'What overtook it',
    onConfirm: (reason) => run('dismiss', () => api.dismiss(card.id, reason || 'overtaken by events')),
  });

  // fix-67: a sales lead ends as Called (what happened, recorded) or Not a lead (a Reject: the rule misfired). fix-81: so
  // does a customer who called our sales line, as Called or Not needed; a card from a call shows the call, not a reply.
  const lead = card.type === 'sales_lead' || card.type === 'customer_called';
  const fromCall = card.payload.source === 'call';
  const notWord = card.type === 'sales_lead' ? 'Not a lead' : 'Not needed';
  const called = () => onConfirm({
    title: 'Record the call',
    detail: fromCall ? 'What happened is recorded as a note on the card.'
      : 'What happened is recorded as a note on the lead. From now on no reply may promise them a call.',
    confirmLabel: 'Called',
    askReason: true,
    reasonLabel: 'What happened on the call (required, recorded)',
    onConfirm: (outcome) => {
      if (!outcome?.trim()) { setProblem('Called needs what happened on the call.'); return; }
      return run('called', async () => started(await api.called(card.id, outcome.trim())));
    },
  });
  const notALead = () => onConfirm({
    title: `${notWord}?`,
    detail: fromCall ? 'Recorded as a Reject: the call was read as needing a call back and did not.'
      : 'Recorded as a Reject: the rule raised a lead that is not one. Any reply still promising a call is then refused until edited.',
    confirmLabel: notWord,
    tone: 'danger',
    onConfirm: () => run('reject', () => api.reject(card.id, notWord.toLowerCase())),
  });

  const signup = signupButtons(card);
  // fix-79: refused before the claim -> the reason here; claimed -> the Desk follows the run and moves on.
  const started = (r: Started) => {
    if (!r.ok) throw new Error(`${r.reason ?? 'refused'}${r.next ? ` — ${r.next}` : ''}`);
    if (r.running) onStarted?.(card);
  };

  // A signup its checks rejected: the account stays Pending, and its email,
  // phone and IP go on the blacklist so the same person is caught next time.
  const confirmRejection = () => onConfirm({
    title: 'Confirm the rejection?',
    detail: `${who(card)} stays Pending in the portal and is never activated. The signup's email, `
          + 'phone and IP go on the blacklist with this account as the source.',
    confirmLabel: 'Confirm rejection',
    tone: 'danger',
    onConfirm: () => run('decline', async () => started(await api.confirmRejection(card.id))),
  });

  // The checks said no; a person says yes. The reason is the record of why,
  // kept on the card and the activated event, and counted on Classes.
  const override = () => onConfirm({
    title: 'Override the checks and activate?',
    detail: 'Activating runs the signup sequence and applies the trial credit. The reason is '
          + 'recorded on the card and the activated event, and counted on the Classes screen.',
    confirmLabel: 'Override and activate',
    tone: 'danger',
    askReason: true,
    reasonLabel: 'Why the checks were wrong (required, recorded)',
    onConfirm: (reason) => {
      if (!reason?.trim()) { setProblem('An override needs a written reason.'); return; }
      return run('override', async () => started(await api.overrideSignup(card.id, reason.trim())));
    },
  });

  const retry = () => onConfirm({
    title: 'Put this card back in the queue?',
    detail: `It failed with: ${card.error_text ?? 'no reason recorded'}. Retrying does not repeat `
          + 'anything that already happened — pre-flight runs again, and the steps that must not '
          + 'happen twice are guarded by events rather than by the card.',
    confirmLabel: 'Retry',
    onConfirm: () => run('retry', () => api.retry(card.id)),
  });

  // fix-82: a failed card whose work was finished by hand (or by the portal, late). Dismissed with the note; nothing runs.
  const closeByHand = () => onConfirm({
    title: 'Close this card — done by hand?',
    detail: `It failed with: ${card.error_text ?? 'no reason recorded'}. Closing runs nothing and sends nothing: `
          + 'the card is dismissed (counted against nothing) and your note is recorded on it and the account.',
    confirmLabel: 'Close card',
    askReason: true,
    reasonLabel: 'What was done by hand (required, recorded)',
    onConfirm: (note) => {
      if (!note?.trim()) { setProblem('Say what was done by hand — the note is required.'); return; }
      return run('close', () => api.closeDoneByHand(card.id, note.trim()));
    },
  });

  const tryNow = () => onConfirm({
    title: 'Try this card now?',
    detail: 'It runs now instead of at the next ten-minute try, with a new portal token. Steps already done in the '
          + 'portal are skipped, so nothing happens twice. If the portal still does not answer, it goes on waiting.',
    confirmLabel: 'Try now',
    onConfirm: () => run('trynow', async () => {
      setStillWaiting(null);
      const r = await api.tryNow(card.id);
      if (r.running) onStarted?.(card); // fix-79: claimed; the tray follows the run
      else if (r.waiting) setStillWaiting(`Still waiting: ${r.reason ?? 'the portal did not answer'}`);
    }),
  });

  // S save · A approve · D dismiss · R reject outside the editor; ⌘↩ sends and ⌘S saves from anywhere; Esc leaves
  // the field (lib/editor.ts keyAction). A letter typed into the draft is a letter. Not while a dialog is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest('[role="dialog"]')) return;
      const inField = !!el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
      const act = keyAction({ key: e.key, meta: e.metaKey, ctrl: e.ctrlKey, alt: e.altKey, inField });
      if (!act) return;
      if (act === 'leave') { el?.blur(); return; }
      // fix-74: the room around the card, on any card, decided or not.
      if (act === 'close') { if (slideOpen) { e.preventDefault(); setSlideOpen(false); } return; }
      if (act === 'next' || act === 'prev' || act === 'queue' || act === 'focus') {
        if (!onLayout || (act === 'focus' && !written)) return;
        e.preventDefault();
        onLayout(act);
        return;
      }
      if (!pending || busy !== null || ed.phase !== 'idle') return;
      // A rejected signup and a sales lead have only their own buttons; no key decides them.
      if (signup === 'rejected' || lead) return;
      if (act === 'save') { e.preventDefault(); if (written) void save(); return; }
      e.preventDefault();
      if (act === 'approve') approve();
      if (act === 'reject') reject();
      if (act === 'dismiss' && signup === 'usual') dismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const context = (
    <div className="detail-col detail-context">
      <div className="detail-head">
        <div className="detail-meta">
          <span className={`badge mono badge-${look.tone}`}>{look.label}</span>
          <span className="mono detail-meta-line">
            card #{card.id} · raised {ageOf(card.created_at)} ago
            {card.status !== 'pending' ? ` · ${card.status}` : ''}
          </span>
          {isConsequential(card) && (
            // Said on the card itself. T31 suspends an account on send,
            // and which templates do that is not something the operator
            // should have to hold in their head.
            <span className="mono detail-held">Consequential · always held for you</span>
          )}
        </div>
        {/* A card about no account and no email (the weekly cards) is named by its title. */}
        <h2>{card.account_no || card.email_id !== null ? who(card) : card.title}</h2>
        {(card.account_no || card.email_id !== null) && <p className="detail-title">{card.title}</p>}
        <span className="mono detail-account-line">{card.customer_email ?? card.payload.to ?? '—'}</span>
        <StateChips card={card} />
        {card.workspace?.trial_history && <TrialSoFar h={card.workspace.trial_history} />}
        <SinceRaised pf={pf.data} loading={pf.loading} />
        {pending && card.type === 'email_draft' && !card.account_no && card.email_id != null && (
          <AssignSender card={card} busy={busy} run={run} onConfirm={onConfirm} />
        )}
      </div>

      {card.email_id !== null && (
        <Fold id={card.id} title="What they wrote"
          summary={email.data?.email.body_text?.trim().replace(/\s+/g, ' ').slice(0, 140) ?? null}
          note={<span className="mono panel-note">
            {email.data
              ? `${whenLocal(email.data.email.received_at)} · thread of ${email.data.email.thread_count}`
              : email.loading ? 'reading…' : '—'}
            {card.payload.attachments_read?.length ? ` · ${card.payload.attachments_read.length} attachment${card.payload.attachments_read.length === 1 ? '' : 's'} read` : ''}
          </span>}>
          <p className="panel-prose">
            {email.data?.email.body_text?.trim()
              || (email.error ? email.error.message : 'The stored message has no text.')}
          </p>
          {card.payload.attachments_read?.length ? <Attachments read={card.payload.attachments_read} /> : null}
          {email.data?.email.thread?.length ? (
            <p className="thread-line">Earlier in thread:{' '}
              {email.data.email.thread.map((t, i) => (
                <span key={t.id}>{i ? ' · ' : ''}<span className="mono">{whenLocal(t.received_at)}</span>
                  {t.direction === 'outbound' ? ' we wrote ' : ' '}&ldquo;{t.opening.slice(0, 80)}{t.opening.length > 80 ? '…' : ''}&rdquo;</span>
              ))}
            </p>
          ) : null}
        </Fold>
      )}

      {card.workspace?.drafter_read && <DrafterRead id={card.id} read={card.workspace.drafter_read} />}

      <details className="panel details" open={!written}>
        <summary>
          <h3>{written ? 'Details' : 'What I checked'}</h3>
          <span className="mono panel-note">{card.evidence.length} checks · every fact carries its age</span>
        </summary>
        {card.evidence.length === 0 ? (
          <p className="panel-prose">Nothing was recorded on this card.</p>
        ) : (
          <ul className="evidence">
            {card.evidence.map((line, i) => (
              <li key={i}>
                {/* No chip when there is no separate age: the drafter writes it into the sentence. */}
                {line.observed && <span className="mono evidence-age">{line.observed}</span>}
                <span>{line.fact}</span>
              </li>
            ))}
          </ul>
        )}
      </details>
    </div>
  );

  const changes = proposedChanges(card);
  const sends = Boolean(card.payload.body || card.payload.template_id);

  return (
    <main className="detail" aria-label={`Card ${card.id}`}>
      <div className="detail-scroll">
        {onBack && (
          <button type="button" className="detail-back" onClick={onBack} aria-label="Back to the queue">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
            <span className="mono">Queue</span>
          </button>
        )}

        {card.status !== 'pending' && (
          <div className="detail-decided mono">
            {card.status} · decided by {decidedBy(card.decided_by, me)}
            {card.decided_at ? ` on ${dayLocal(card.decided_at)}` : ''}
            {card.error_text ? ` — ${card.error_text}` : ''}
          </div>
        )}

        <div className={`detail-columns ${focus ? 'is-focus' : ''}`}>
          {/* LEFT, in reading order: who and their live state; pre-flight; what they wrote; what the drafter read.
              In focus (fix-74) it is the slide-over instead, rendered below outside the scroller. */}
          {!focus && context}
          {focus && (
            <p className="focus-who mono">
              <span className={`badge mono badge-${look.tone}`}>{look.label}</span>
              <span>{who(card)} · card #{card.id} · raised {ageOf(card.created_at)} ago</span>
            </p>
          )}

          {/* RIGHT, the workspace. */}
          <div className="detail-col detail-work">
            {lead && fromCall ? (
              <section className="panel panel-grow panel-draft">
                <div className="panel-head">
                  <h3>The call</h3>
                  <span className="mono panel-note">{card.payload.called_at ? `${card.payload.called_at} UTC` : ''}</span>
                </div>
                <p className="panel-prose"><strong>{card.payload.phone}</strong> — about {card.payload.topic}</p>
                <p className="panel-prose">{card.payload.summary}</p>
                <p className="panel-prose">The transcript was removed once this card was raised (fix-81).</p>
              </section>
            ) : lead ? (
              <section className="panel panel-grow panel-draft">
                <div className="panel-head">
                  <h3>The reply</h3>
                  <span className="mono panel-note">
                    {card.lead_draft ? `card #${card.lead_draft.id} · ${card.lead_draft.status}` : 'not drafted yet'}
                  </span>
                </div>
                {card.payload.callback && (
                  <p className="panel-prose">While this lead is open the reply may say, word for word:
                    {' '}<strong>{card.payload.callback}</strong></p>
                )}
                {card.lead_draft?.body
                  ? <p className="panel-prose">{card.lead_draft.body}</p>
                  : <p className="panel-prose">No reply has been drafted to this lead yet.</p>}
                {card.lead_draft && (
                  <button type="button" className="btn-ghost btn-small" onClick={() => setLocation(`/cards/${card.lead_draft!.id}`)}>
                    Open the reply
                  </button>
                )}
              </section>
            ) : card.type === 'drafter_edits' ? (
              <EditsPanel card={card} />
            ) : written ? (
              <>
                <QuestionsList card={card} />
                <AnswersPanel card={card} pending={pending} busy={busy !== null || ed.phase !== 'idle'}
                  onAnswer={answer} run={run} draftFocused={draftFocused} />
                {card.type === 'email_draft' && (card.escalation || (pending && operator)) && (
                  <EscalationPanel card={card} pending={pending} operator={operator} busy={busy !== null}
                    onSet={(undo) => run(undo ? 'unescalate' : 'escalate', () => api.escalate(card.id, undo))} />
                )}
                <DraftWorkspace card={card} ed={ed} pending={pending} check={check?.result ?? null}
                  checkedFor={check?.for ?? null} editorRef={editorRef}
                  focus={onLayout ? focus : null} onFocusToggle={() => onLayout?.('focus')}
                  onContext={() => setSlideOpen(true)} onEditorFocus={() => setDraftFocused(true)}
                  onType={(fields) => dispatch({ type: 'typed', fields })} onRefine={refineWith}
                  onDiscard={() => dispatch({ type: 'discard' })} />
              </>
            ) : sends ? (
              <section className="panel panel-grow panel-draft">
                <div className="panel-head">
                  <h3>Template</h3>
                  <span className="mono panel-note">template {card.payload.template_id} · rendered on send</span>
                </div>
                <p className="panel-prose">{card.payload.subject ?? '(from the template)'}</p>
                <p className="panel-prose">This card sends a lifecycle template. Its wording lives in the template, not on the card.</p>
              </section>
            ) : changes.length === 0 ? (
              <section className="panel panel-grow">
                <div className="panel-head"><h3>What approving does</h3></div>
                <p className="panel-prose">
                  This card sends nothing and changes nothing. Approving it records that you have
                  read it.
                </p>
              </section>
            ) : null}

            {changes.length > 0 && (
              <section className="panel panel-change">
                <div className="panel-head">
                  <h3>Proposed account change</h3>
                  <span className="mono panel-tag">read back from the portal after each step</span>
                </div>
                <div className="changes">
                  {changes.map((c) => (
                    <div key={c.field} className="change">
                      <div className="mono change-field">{c.field}</div>
                      <div className="change-value">
                        <span className="change-before">{c.before ?? 'unset'}</span>
                        {' → '}
                        <strong>{c.after ?? '—'}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      {focus && slideOpen && (
        <>
          <div className="slideover-scrim" onClick={() => setSlideOpen(false)} aria-hidden="true" />
          <aside className="slideover" aria-label="The card's context">
            <button type="button" className="btn-ghost btn-small slideover-close" onClick={() => setSlideOpen(false)}>
              Close · Esc
            </button>
            {context}
          </aside>
        </>
      )}

      <div className="actions">
        {problem && <div className="actions-problem">{problem}</div>}
        {running && running.phase === 'running' ? (
          // fix-79: approved and claimed; the run continues on the desk, followed here and in the tray.
          <div className="actions-row">
            <span className="mono actions-keys">{runLine(running)}</span>
          </div>
        ) : pending && signup === 'rejected' ? (
          <div className="actions-row">
            <button type="button" className="btn-reject" onClick={confirmRejection} disabled={busy !== null}>
              {busy === 'decline' ? 'Working…' : 'Confirm rejection'}
            </button>
            <button type="button" className="btn-secondary" onClick={override} disabled={busy !== null}>
              {busy === 'override' ? 'Activating…' : 'Override and activate…'}
            </button>
            <span className="mono actions-keys">The checks rejected this signup</span>
          </div>
        ) : pending && lead ? (
          <div className="actions-row">
            <button type="button" className="btn-primary" onClick={called} disabled={busy !== null}>
              {busy === 'called' ? 'Recording…' : 'Called…'}
            </button>
            <button type="button" className="btn-reject" onClick={notALead} disabled={busy !== null}>
              {notWord}
            </button>
            <span className="mono actions-keys">Call them, then say what happened</span>
          </div>
        ) : pending ? (
          // One action bar, left to right in the order they are used (fix-73).
          <div className="actions-row">
            {written && (
              <button type="button" className="btn-secondary" onClick={() => void save()}
                disabled={busy !== null || ed.phase !== 'idle' || !edited}>
                {ed.phase === 'saving' ? 'Saving…' : 'Save my version'}
              </button>
            )}
            <button type="button" className="btn-primary" onClick={approve} disabled={busy !== null || ed.phase !== 'idle'}>
              {busy === 'approve' ? 'Working…' : approveLabel(card, changes.length > 0, sends)}
            </button>
            <span className="actions-sep" aria-hidden="true" />
            {signup === 'usual' && (
              <button type="button" className="btn-dismiss" onClick={dismiss} disabled={busy !== null}>
                Dismiss
              </button>
            )}
            <button type="button" className="btn-reject" onClick={reject} disabled={busy !== null}>
              Reject
            </button>
            <span className="mono actions-keys">
              {[written && 'S save', 'A approve', signup === 'usual' && 'D dismiss', 'R reject', '⌘↩ send',
                onLayout && 'J K cards', onLayout && 'Q queue', onLayout && written && 'F focus'].filter(Boolean).join(' · ')}
            </span>
          </div>
        ) : card.status === 'failed' ? (
          // A failed card is not finished, it is waiting. Retrying is safe because the things that must not happen
          // twice are guarded by events and constraints rather than by a card's status. Pre-flight runs again.
          <div className="actions-row">
            <button type="button" className="btn-primary" onClick={retry} disabled={busy !== null}>
              {busy === 'retry' ? 'Putting it back…' : 'Retry this card'}
            </button>
            {operator && (
              <button type="button" className="btn-dismiss" onClick={closeByHand} disabled={busy !== null}>
                {busy === 'close' ? 'Closing…' : 'Close — done by hand'}
              </button>
            )}
            <span className="mono actions-keys">
              Retry puts it back in the queue; Close records that the work was done by hand.
            </span>
          </div>
        ) : wait ? (
          // fix-75 / #217: approved, and the portal could not take it yet. The drain tries every ten minutes.
          <div className="portal-wait">
            <div className="portal-wait-line">{waitLine(wait)}</div>
            <div className="portal-wait-reason mono">{stillWaiting ?? wait.reason}</div>
            <div className="actions-row">
              {operator && (
                <button type="button" className="btn-primary" onClick={tryNow} disabled={busy !== null}>
                  {busy === 'trynow' ? 'Trying…' : 'Try now'}
                </button>
              )}
              <span className="mono portal-wait-note">
                It finishes on its own when the portal answers. If it cannot wait, do it by hand in the portal — the card skips what is already done.
              </span>
            </div>
          </div>
        ) : (
          <div className="actions-row">
            <span className="mono actions-keys">
              {card.status === 'expired'
                // Not "failed": nothing went wrong and nothing can be retried.
                ? 'This card expired — what it was raised for stopped being true. There is nothing to retry.'
                : `This card is ${card.status}. Nothing here will change it.`}
            </span>
            <button type="button" className="btn-ghost" onClick={() => setLocation('/')}>Back to the queue</button>
          </div>
        )}
      </div>
    </main>
  );
}

function approveLabel(card: Card, hasChanges: boolean, sends: boolean): string {
  if (card.type === 'approve_signup') return 'Approve and activate';
  if (card.type === 'hero_ticket') return 'Done'; // hero-tickets-1 (INBND-68): informational, nothing is sent
  if (hasChanges && sends) return 'Approve, send and change';
  if (hasChanges) return 'Approve and change';
  if (sends) return 'Approve and send';
  return 'Mark as read';
}

/**
 * The field / before / after table.
 *
 * Read from the card's own payload, because the payload is what the executor
 * will act on. A table built from anything else would be showing the operator
 * one thing and running another.
 */
function proposedChanges(card: Card): { field: string; before: string | null; after: string | null }[] {
  if (Array.isArray(card.payload.changes)) return card.payload.changes;

  if (card.type === 'approve_signup') {
    // The signup sequence, in the order the executor runs it. The $11.00 is
    // $12.10 on the account because the portal adds GST, and saying only one
    // of those numbers is how somebody reconciles the wrong one.
    return [
      { field: 'status', before: 'Pending', after: 'Active' },
      { field: 'iou_limit', before: null, after: '$5.00' },
      { field: 'credit', before: null, after: '$11.00 Free Trial Credit ($12.10 on the account)' },
    ];
  }

  return [];
}

/**
 * "Since this card was raised".
 *
 * The strip shows every fact, not only the ones that refuse. Pre-flight
 * answers "may this run" and stops at the first contradiction; the operator
 * needs to know the picture has not moved, and four green facts say that where
 * one red word would not.
 */
function SinceRaised({ pf, loading }: { pf: Preflight | null; loading: boolean }) {
  if (loading && !pf) return <div className="since since-quiet mono">Checking what has changed…</div>;
  if (!pf) return null;

  return (
    <div className={`since ${pf.ok ? '' : 'since-refuse'}`}>
      <span className={`since-verdict ${pf.ok ? 'is-ok' : 'is-refuse'}`}>
        ● {pf.ok
          ? 'Pre-flight would pass'
          // Said before the click. Cards 23, 146 and 427 all read "would refuse"
          // and sat together in Failed, and only one of them could ever succeed.
          : pf.permanent ? 'Can no longer succeed' : 'Pre-flight would refuse'}
      </span>
      <span className="since-checks">
        {pf.ok ? 'since raised: ' : ''}
        {pf.checks.map((c, i) => (
          <span key={c.key} className="since-check" title={c.detail}>
            {i ? ' · ' : ''}<span className={`dot dot-${c.state === 'refuse' ? 'bad' : c.state === 'note' ? 'warn' : 'ok'}`} />{c.label}
          </span>
        ))}
      </span>
      {!pf.ok && pf.reason && <div className="since-why">{pf.reason}. {pf.next}</div>}
    </div>
  );
}

/** One thumbnail; after 30 days the desk has deleted the bytes, and the tile says so instead of a broken image. */
function Thumb({ a }: { a: AttachmentRead }) {
  const [gone, setGone] = useState(false);
  if (gone) return <span className="attachment-thumb attachment-pdf mono">No longer stored</span>;
  return (
    <a href={attachmentUrl(a.id)} target="_blank" rel="noreferrer" className="attachment-thumb">
      {a.kind === 'image'
        ? <img src={attachmentUrl(a.id)} alt={a.filename} loading="lazy" onError={() => setGone(true)} />
        : <span className="attachment-pdf mono">PDF{a.pages ? ` · ${a.pages} p` : ''}</span>}
    </a>
  );
}

/**
 * What the customer attached that the drafter read (fix-27): each image as a thumbnail beside what the drafter said it
 * saw, so a misreading is visible before approving. Served by the desk from R2 and gone after 30 days.
 */
function Attachments({ read }: { read: AttachmentRead[] }) {
  return (
    <ul className="attachments">
      {read.map((a) => (
        <li key={a.id}>
          <Thumb a={a} />
          <div className="attachment-text">
            <span className="mono attachment-name">{a.label} · {a.filename}</span>
            <span>{a.saw ?? 'The drafter did not say what it saw.'}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Who and their live state (fix-73): status and when they last paid, the balance with its age, how lines were read. */
function StateChips({ card }: { card: Card }) {
  const s = card.workspace?.state;
  if (!s) return null;
  return (
    <div className="chips mono">
      <span className={`chip ${s.status === 'Active' ? 'chip-ok' : ''}`}>
        {s.status ?? 'no status'}{s.last_paid_at ? ` · paid ${dayLocal(s.last_paid_at)}` : ' · never paid'}
      </span>
      {s.balance !== null && <span className="chip">balance {usd(s.balance)}{s.balance_age ? ` · ${s.balance_age}` : ''}</span>}
      <span className="chip">{s.lines}</span>
    </div>
  );
}

/**
 * Trial so far (fix-83, Royce 19 Oct, T31 card 1621): one line per thing that happened on a never-paid account, oldest
 * first, in Brisbane time with the trial day — each email with its card, each reply against the email it answers, the
 * templates passed without and why — and where it is now. The desk writes the words; this lays them out.
 */
// escalated-1 (ADR-0361, DRAFT-73/74): a fault passed to our voice engineers. While escalated the reply may say so in
// one sentence, built by the desk, word for word and once; Refine is told it. Operator only; Undo while pending.
function EscalationPanel({ card, pending, operator, busy, onSet }: {
  card: Card; pending: boolean; operator: boolean; busy: boolean; onSet: (undo: boolean) => void;
}) {
  const e = card.escalation;
  return (
    <section className="panel">
      <div className="panel-head">
        <h3>Escalated to engineers</h3>
        <span className="mono panel-note">{e ? `by ${e.by} · ${whenBrisbane(e.at)}` : 'not escalated'}</span>
      </div>
      {e ? (
        <p className="panel-prose">The reply may say, once and word for word: <strong>{e.sentence}</strong></p>
      ) : (
        <p className="panel-prose">If this is a platform fault passed to our voice engineers, mark it: the reply may then say
          so in one fixed sentence. Nothing is sent.</p>
      )}
      {pending && operator && (
        <button type="button" className="btn-ghost btn-small" disabled={busy} onClick={() => onSet(Boolean(e))}>
          {e ? 'Undo escalation' : 'Escalated to engineers'}
        </button>
      )}
    </section>
  );
}

function TrialSoFar({ h }: { h: NonNullable<NonNullable<Card['workspace']>['trial_history']> }) {
  const day = (d: number | null) => (d === null ? '' : `day ${d}`);
  return (
    <section className="trial" aria-label="Trial so far">
      <h3 className="trial-title mono">Trial so far</h3>
      <ol className="trial-lines">
        {h.lines.map((l, i) => (
          <li key={i} className={`trial-line trial-${l.kind}`}>
            <span className="mono trial-when">{whenBrisbane(l.at)}</span>
            <span className="mono trial-day">{day(l.day)}</span>
            <span className="trial-text">
              {l.text}
              {l.card_id !== null && <> · <Link href={`/cards/${l.card_id}`} className="mono">card #{l.card_id}</Link></>}
            </span>
          </li>
        ))}
        <li className="trial-line trial-now">
          <span className="mono trial-when">Now</span>
          <span className="mono trial-day">{day(h.now.day)}</span>
          <span className="trial-text">{h.now.title ?? `card #${h.now.card_id}`} · {h.now.facts}</span>
        </li>
      </ol>
    </section>
  );
}

/** What the drafter read (fix-73): the pages and sections used, what it read live, and the inferred sentences. */
function DrafterRead({ id, read }: { id: number; read: NonNullable<NonNullable<Card['workspace']>['drafter_read']> }) {
  const summary = [
    read.pages.length && `${read.pages.length} section${read.pages.length === 1 ? '' : 's'}`,
    read.links.length && `${read.links.length} link${read.links.length === 1 ? '' : 's'}`,
    read.live.length && `${read.live.length} live read${read.live.length === 1 ? '' : 's'}`,
    read.inferred > 0 && `▲ ${read.inferred} inferred`,
  ].filter(Boolean).join(' · ') || 'no section and no live read';
  return (
    <Fold id={id} title="What the drafter read" summary={summary}>
      <ul className="read-list">
        {read.pages.map((p) => <li key={p}>{p}</li>)}
        {read.links.map((l) => <li key={l}>Linked: {l}</li>)}
        {read.live.map((l) => <li key={l}>Live read: {l}</li>)}
        {!read.pages.length && !read.live.length && <li>No knowledge section and no live read.</li>}
      </ul>
      {read.inferred > 0 && (
        <p className="read-inferred">▲ {read.inferred} inferred sentence{read.inferred === 1 ? '' : 's'} (highlighted in the
          draft){read.inferred_from.length ? ` · sources: ${read.inferred_from.join('; ')}` : ''}</p>
      )}
    </Fold>
  );
}

/**
 * A context panel that folds to one line when the card is narrower than its two columns (fix-74): the container query
 * in carddetail.css shows the toggle and the summary and hides the body unless opened. In two columns it is the plain
 * panel it always was, open. Starts folded for each card.
 */
function Fold({ id, title, summary, note, children }: {
  id: number; title: string; summary: string | null; note?: React.ReactNode; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [id]);
  return (
    <section className={`panel fold ${open ? 'is-open' : ''}`}>
      <div className="panel-head">
        <h3>{title}</h3>
        {note}
        <button type="button" className="fold-toggle mono" onClick={() => setOpen((x) => !x)} aria-expanded={open}>
          {open ? 'hide' : 'show'}
        </button>
      </div>
      {summary && <p className="fold-summary" onClick={() => setOpen(true)}>{summary}</p>}
      <div className="fold-body">{children}</div>
    </section>
  );
}

// assign-1: a sender no rule could match (a customer writing from another address) is matched to an account by hand.
// The exact address is remembered, every unmatched email from it joins the account, and this one is drafted again.
function AssignSender({ card, busy, run, onConfirm }: {
  card: Card;
  busy: string | null;
  run: (label: string, fn: () => Promise<unknown>) => Promise<void>;
  onConfirm: (r: ConfirmRequest) => void;
}) {
  const [acct, setAcct] = useState('');
  const [lookup, setLookup] = useState<string | null>(null);
  const find = async () => {
    setLookup(null);
    try {
      const { customer } = await api.customer(acct.trim());
      const sender = card.payload.to ?? 'this sender';
      onConfirm({
        title: `Match ${sender} to ${customer.name ?? customer.account_no}?`,
        detail: `Account ${customer.account_no} · ${customer.status ?? 'no status'} · ${customer.email ?? 'no email'}. `
          + `Every email from ${sender} will be filed under this account from now on, the ones already here move now, `
          + 'and this reply is drafted again with the account.',
        confirmLabel: 'Assign',
        onConfirm: () => run('assign', () => api.assign(card.id, customer.account_no)),
      });
    } catch {
      setLookup(`There is no account ${acct.trim()}.`);
    }
  };
  return (
    <form className="assign-sender" onSubmit={(e) => { e.preventDefault(); if (acct.trim()) void find(); }}>
      <label className="mono" htmlFor={`assign-${card.id}`}>Assign to customer</label>
      <div className="assign-row">
        <input id={`assign-${card.id}`} inputMode="numeric" placeholder="Account number" value={acct}
          onChange={(e) => setAcct(e.target.value)} disabled={busy !== null} />
        <button type="submit" className="btn-secondary" disabled={busy !== null || !acct.trim()}>Find</button>
      </div>
      {lookup && <p className="assign-problem">{lookup}</p>}
    </form>
  );
}
